import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import PropTypes from "prop-types";

import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Icon from "@mui/material/Icon";
import MenuIcon from "@mui/icons-material/Menu";
import { useTheme } from "@mui/material/styles";

import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import Breadcrumbs from "examples/Breadcrumbs";
import NotificationItem from "examples/Items/NotificationItem";

import {
  useMaterialUIController,
  setTransparentNavbar,
  setMiniSidenav,
  setOpenConfigurator,
} from "context";

// ✅ on utilise le service commun
import { logout as apiLogout } from "services/session";

function DashboardNavbar({ absolute, light, isMini }) {
  const [navbarType, setNavbarType] = useState();
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, transparentNavbar, fixedNavbar, openConfigurator } = controller;
  const [openMenu, setOpenMenu] = useState(false);
  const [openUserMenu, setOpenUserMenu] = useState(null);
  const route = useLocation().pathname.split("/").slice(1);
  const theme = useTheme();

  useEffect(() => {
    setNavbarType(fixedNavbar ? "sticky" : "static");
    const handleTransparentNavbar = () => {
      setTransparentNavbar(dispatch, (fixedNavbar && window.scrollY === 0) || !fixedNavbar);
    };
    window.addEventListener("scroll", handleTransparentNavbar);
    handleTransparentNavbar();
    return () => window.removeEventListener("scroll", handleTransparentNavbar);
  }, [dispatch, fixedNavbar]);

  const handleMiniSidenav = () => setMiniSidenav(dispatch, !miniSidenav);
  const handleConfiguratorOpen = () => setOpenConfigurator(dispatch, !openConfigurator);
  const handleOpenMenu = (event) => setOpenMenu(event.currentTarget);
  const handleCloseMenu = () => setOpenMenu(false);
  const handleUserMenuOpen = (event) => setOpenUserMenu(event.currentTarget);
  const handleUserMenuClose = () => setOpenUserMenu(null);

  // ✅ Déconnexion unifiée via services/session
  const handleLogoutClick = async () => {
    try {
      setOpenUserMenu(null); // fermer le menu d'abord
      await apiLogout();
    } finally {
      // Nettoyage côté front
      localStorage.removeItem("token");
      localStorage.removeItem("user_id");
      localStorage.removeItem("company");
      // Redirection
      window.location.href = "/authentication/sign-in";
    }
  };

  const iconButtonStyle = {
    backgroundColor: "#fff",
    borderRadius: "50%",
    boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
    transition: "all 0.2s ease-in-out",
    "&:hover": { boxShadow: "0 4px 12px rgba(0,0,0,0.15)" },
    padding: "8px",
  };

  const iconStyle = { fontSize: "2rem", color: "#111" };

  return (
    <AppBar
      position="static"
      sx={{ width: "100%", boxShadow: "none", borderRadius: "16px", mb: 2 }}
    >
      <Toolbar
        sx={{
          width: "100%",
          display: "flex",
          justifyContent: "space-between",
          px: 2,
          flexWrap: "wrap",
        }}
      >
        {/* Gauche */}
        <MDBox display="flex" alignItems="center" gap={1} sx={{ flexShrink: 0 }}>
          <IconButton onClick={handleMiniSidenav} sx={iconButtonStyle}>
            <MenuIcon sx={iconStyle} />
          </IconButton>
          <Breadcrumbs
            icon="home"
            title={route[route.length - 1] || "Dashboard"}
            route={route}
            light={light}
          />
        </MDBox>

        {/* Droite */}
        <MDBox display="flex" alignItems="center" gap={2}>
          <MDInput
            label="Search"
            variant="standard"
            sx={{ minWidth: 200, display: { xs: "none", md: "block" } }}
          />

          <IconButton onClick={handleOpenMenu} sx={iconButtonStyle}>
            <Icon sx={iconStyle}>notifications</Icon>
          </IconButton>
          <Menu anchorEl={openMenu} open={Boolean(openMenu)} onClose={handleCloseMenu}>
            <NotificationItem icon="email" title="Nouveau message" />
            <NotificationItem icon="podcasts" title="Podcast disponible" />
          </Menu>

          <IconButton onClick={handleUserMenuOpen} sx={iconButtonStyle}>
            <Icon sx={iconStyle}>account_circle</Icon>
          </IconButton>
          <Menu anchorEl={openUserMenu} open={Boolean(openUserMenu)} onClose={handleUserMenuClose}>
            <MenuItem onClick={handleLogoutClick}>Déconnexion</MenuItem>
          </Menu>
        </MDBox>
      </Toolbar>
    </AppBar>
  );
}

DashboardNavbar.defaultProps = { absolute: false, light: false, isMini: false };
DashboardNavbar.propTypes = {
  absolute: PropTypes.bool,
  light: PropTypes.bool,
  isMini: PropTypes.bool,
};

export default DashboardNavbar;
